/**
 * Maps ASP.NET ProblemDetails `errors` (keys like `beneficial_owners[0]`)
 * into a human banner — translated when we know the message, otherwise joined.
 */

import intl from "react-intl-universal";

const OWNER_FIELD_RE = /^beneficial_owners\[(\d+)\](?:\.(.+))?$/i;

const KNOWN_MESSAGES = [
  {
    test: /a legal responsible and beneficial owners are required/i,
    key: "baas.accounts.create.legalResponsibleRequired",
    fallback:
      "A entidade precisa de ao menos um responsável legal (pessoa de controle) e beneficiário final. Vincule um sócio com Pessoa de controle na empresa e tente novamente.",
    standalone: true,
  },
  {
    test: /a legal responsible is required/i,
    key: "baas.accounts.create.legalResponsibleRequired",
    fallback:
      "A entidade precisa de ao menos um responsável legal (pessoa de controle) e beneficiário final. Vincule um sócio com Pessoa de controle na empresa e tente novamente.",
    standalone: true,
  },
  {
    test: /provide at least one document:\s*passport,\s*drivers_license,\s*or\s*national_id/i,
    key: "baas.entidades.validation.ownerDocumentRequired",
    fallback: "Sócio {index}: informe pelo menos um documento (RG, CNH ou passaporte).",
    ownerScoped: true,
  },
  {
    test: /provide at least one document:\s*ssn,\s*passport,\s*drivers_license,\s*or\s*national_id/i,
    key: "baas.entidades.validation.personDocumentRequired",
    fallback: "Informe pelo menos um documento (CPF, RG, CNH ou passaporte).",
  },
  {
    test: /this person is already a beneficial owner/i,
    key: "baas.entidades.validation.ownerAlreadyLinked",
    fallback: "Sócio {index}: esta pessoa já é sócia desta empresa.",
    ownerScoped: true,
  },
  {
    test: /at least one beneficial owner is required/i,
    key: "baas.entidades.validation.ownerRequired",
    fallback: "Informe pelo menos um sócio.",
  },
  {
    test: /beneficial_owners cannot contain more than/i,
    key: "baas.entidades.validation.ownerMax",
    fallback: "Há sócios demais nesta empresa.",
  },
  {
    test: /a person with this document already exists/i,
    key: "baas.entidades.validation.documentAlreadyExists",
    fallback: "Já existe uma pessoa com este documento.",
  },
  {
    test: /this document is already registered/i,
    key: "baas.entidades.validation.documentRegistered",
    fallback: "Este documento já está cadastrado.",
  },
  {
    test: /an account with this document already exists/i,
    key: "baas.entidades.validation.accountDocumentExists",
    fallback: "Já existe uma conta com este documento.",
  },
  {
    test: /a business entity with this document already exists/i,
    key: "baas.entidades.validation.businessDocumentExists",
    fallback: "Já existe uma empresa com este documento.",
  },
  {
    test: /mother_name is required/i,
    key: "baas.entidades.validation.motherNameRequired",
    fallback: "Informe o nome da mãe.",
  },
  {
    test: /phone_number must be in e\.164/i,
    key: "baas.entidades.validation.phoneE164",
    fallback: "Telefone deve estar no formato internacional (ex.: +5511999999999).",
  },
  {
    test: /date_of_birth must be in yyyy-mm-dd/i,
    key: "baas.entidades.validation.dateOfBirthFormat",
    fallback: "Data de nascimento deve estar no formato AAAA-MM-DD.",
  },
  {
    test: /expiration_date must be in yyyy-mm-dd/i,
    key: "baas.entidades.validation.expirationFormat",
    fallback: "Validade deve estar no formato AAAA-MM-DD.",
  },
  {
    test: /country_code must be iso 3166-1 alpha-2/i,
    key: "baas.entidades.validation.countryIso2",
    fallback: "País deve ser o código ISO de 2 letras (ex.: BR, US).",
  },
  {
    test: /country_of_citizenship must be iso 3166-1 alpha-2/i,
    key: "baas.entidades.validation.citizenshipIso2",
    fallback: "Nacionalidade deve ser o código ISO de 2 letras (ex.: BR, US).",
  },
  {
    test: /number is required when the document is provided/i,
    key: "baas.entidades.validation.documentNumberRequired",
    fallback: "Informe o número do documento.",
  },
  {
    test: /must not be empty/i,
    key: "baas.entidades.validation.required",
    fallback: "Campo obrigatório.",
  },
];

const FIELD_LABEL_KEYS = {
  national_id: "baas.entidades.conflict.field.national_id",
  passport: "baas.entidades.conflict.field.passport",
  drivers_license: "baas.entidades.conflict.field.drivers_license",
  ssn: "baas.entidades.conflict.field.ssn",
  ein: "baas.entidades.conflict.field.ein",
  registration_id: "baas.entidades.conflict.field.registration_id",
  national_registration: "baas.entidades.conflict.field.national_registration",
  first_name: "baas.entidades.validation.field.firstName",
  last_name: "baas.entidades.validation.field.lastName",
  email: "baas.entidades.validation.field.email",
  phone_number: "baas.entidades.validation.field.phone",
  date_of_birth: "baas.entidades.validation.field.dateOfBirth",
  mother_name: "baas.entidades.validation.field.motherName",
  address: "baas.entidades.validation.field.address",
  country_code: "baas.entidades.validation.field.countryCode",
  number: "baas.entidades.validation.field.documentNumber",
};

const FIELD_LABEL_FALLBACKS = {
  national_id: "RG / CPF",
  passport: "passaporte",
  drivers_license: "CNH",
  ssn: "SSN",
  ein: "EIN",
  registration_id: "número de registro",
  national_registration: "documento (CNPJ/CPF)",
  first_name: "nome",
  last_name: "sobrenome",
  email: "e-mail",
  phone_number: "telefone",
  date_of_birth: "data de nascimento",
  mother_name: "nome da mãe",
  address: "endereço",
  country_code: "país",
  number: "número do documento",
};

const interpolate = (template, vars = {}) =>
  String(template).replace(/\{(\w+)\}/g, (_, name) =>
    vars[name] == null ? `{${name}}` : String(vars[name]),
  );

const t = (key, vars, fallback) => {
  try {
    const localeReady = Boolean(intl.getInitOptions?.()?.currentLocale);
    if (localeReady) {
      const value = intl.get(key, vars);
      if (value && value !== key) return value;
    }
  } catch {
    // Locale may be uninitialized in unit tests.
  }
  return interpolate(fallback || "", vars);
};

const tryParseJsonObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const parseAspNetFieldKey = (field) => {
  const raw = String(field || "").replace(/^\$\.?/, "").trim();
  const ownerMatch = OWNER_FIELD_RE.exec(raw);
  if (ownerMatch) {
    return {
      raw,
      ownerIndex: Number(ownerMatch[1]),
      path: ownerMatch[2] ? ownerMatch[2].toLowerCase() : "",
    };
  }
  return { raw, ownerIndex: null, path: raw.toLowerCase() };
};

export const collectProblemDetailsErrors = (error) => {
  const nestedDetail = tryParseJsonObject(error?.detail || error?.data?.detail);
  const candidates = [
    error?.errors,
    error?.data?.errors,
    error?.response?.data?.errors,
    nestedDetail?.errors,
  ];
  const raw = candidates.find(
    (item) => item && typeof item === "object" && !Array.isArray(item),
  );
  if (!raw) return [];

  return Object.entries(raw).flatMap(([field, value]) => {
    const list = Array.isArray(value) ? value : [value];
    return list
      .map((entry) => String(entry || "").trim())
      .filter(Boolean)
      .map((message) => ({ field, message, parsed: parseAspNetFieldKey(field) }));
  });
};

const leafPath = (path) => {
  const parts = String(path || "").split(".").filter(Boolean);
  return parts[parts.length - 1] || "";
};

const fieldLabel = (parsed) => {
  const leaf = leafPath(parsed.path);
  if (!leaf) return "";
  const key = FIELD_LABEL_KEYS[leaf];
  const fallback = FIELD_LABEL_FALLBACKS[leaf] || leaf.replace(/_/g, " ");
  return key ? t(key, {}, fallback) : fallback;
};

const ownerPrefix = (index) =>
  t(
    "baas.entidades.validation.ownerLabel",
    { index: index + 1 },
    "Sócio {index}",
  );

const translateMessage = (entry) => {
  const { message, parsed } = entry;
  const ownerIndex = parsed.ownerIndex == null ? 1 : parsed.ownerIndex + 1;
  const known = KNOWN_MESSAGES.find((item) => item.test.test(message));
  if (known) {
    return {
      text: t(known.key, { index: ownerIndex }, known.fallback),
      standalone: Boolean(
        known.standalone || (known.ownerScoped && parsed.ownerIndex != null),
      ),
    };
  }
  return { text: message, standalone: false };
};

export const getBeneficialOwnerIndexesFromError = (error) => {
  const seen = new Set();
  collectProblemDetailsErrors(error).forEach((entry) => {
    if (entry.parsed.ownerIndex != null) seen.add(entry.parsed.ownerIndex);
  });
  return [...seen];
};

/**
 * Human-readable ProblemDetails `errors` for the entity save banner.
 * @returns {string|null} null when there is no `errors` object
 */
export const formatBaasEntityValidationMessage = (error) => {
  const entries = collectProblemDetailsErrors(error);
  if (entries.length === 0) return null;

  const lines = entries.map((entry) => {
    const translated = translateMessage(entry);
    if (translated.standalone) return translated.text;

    const parts = [];
    if (entry.parsed.ownerIndex != null) {
      parts.push(ownerPrefix(entry.parsed.ownerIndex));
    }
    const label = fieldLabel(entry.parsed);
    if (label) parts.push(label);
    if (parts.length === 0) return translated.text;
    return `${parts.join(" · ")}: ${translated.text}`;
  });

  return lines.filter(Boolean).join(" ");
};

const GENERIC_ASPNET_TITLE = /one or more validation errors occurred/i;

const usableDetail = (value) => {
  const text = String(value || "").trim();
  if (!text || GENERIC_ASPNET_TITLE.test(text)) return "";
  return text;
};

export const formatBaasEntitySaveError = (error, fallback) =>
  formatBaasEntityValidationMessage(error)
  || usableDetail(error?.detail || error?.data?.detail)
  || fallback
  || usableDetail(error?.message)
  || null;
